
import React from 'react';
import { MaterialCategory, DatabaseMaterial } from '@/hooks/useMaterials';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Edit, Trash2, Copy, ArrowUp, ArrowDown, ArrowUpDown, Star, Scale, FileDown, Eye, EyeOff } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import MaterialComparatorDialog from './MaterialComparatorDialog';
import { useFavoriteMaterials, useToggleFavoriteMaterial } from '@/hooks/useFavoriteMaterials';
import { toast } from 'sonner';
import { useDeleteMaterial } from '@/hooks/useMaterialActions';
import { useMaterialDuplicate } from '@/hooks/useMaterialDuplicate';
import { useOrgProfile } from '@/hooks/useOrgSettings';
import { exportMaterialPdf } from '@/utils/export/exportMaterialPdf';
import { useHasFeature } from '@/hooks/useOrgPlan';
import EditMaterialDialog from './EditMaterialDialog';
import CreateMaterialDialog from './CreateMaterialDialog';
import { getScrewPricePerPiece } from '@/utils/screwPricing';
import { MaterialSortField, SortDirection } from '@/hooks/useMaterialsSorting';
import MaterialDetailsCell from "./MaterialDetailsCell";
import MaterialDimensionsCell from "./MaterialDimensionsCell";
import EmptyState from "@/components/common/EmptyState";
import { useWasteFactors } from '@/hooks/useWasteFactors';
import { useMaterialsUsage } from '@/hooks/useMaterialsUsage';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';
import { useSupplierFamilies } from '@/hooks/useSupplierFamilies';
import { useHiddenMaterials, useToggleHiddenMaterial } from '@/hooks/useHiddenMaterials';
import { useIsAppAdmin } from '@/hooks/useIsAppAdmin';
import { ShieldAlert, Lock, UserPlus } from 'lucide-react';
import { useCertifiedUsage } from '@/hooks/useCertifiedUsage';
import { useForkMaterial } from '@/hooks/useForkMaterial';
import { Flag } from 'lucide-react';
import ReportMaterialDialog from './ReportMaterialDialog';
import { formatNumber } from '@/utils/format';

interface MaterialsTableProps {
  materials: DatabaseMaterial[];
  /** Se "Raggruppa per formato" è attivo: id del rappresentante → etichette formato
   *  (es. ["2.0 m", "3.0 m"]). Mostra un badge "N formati" sulla riga raggruppata. */
  formatGroups?: Map<string, string[]>;
  onEditMaterial?: (material: DatabaseMaterial) => void;
  sortField?: MaterialSortField;
  sortDirection?: SortDirection;
  onSortChange?: (field: MaterialSortField) => void;
  /** Set di id selezionati per bulk action. Se omesso, le checkbox sono nascoste. */
  selectedIds?: Set<string>;
  /** Toggle di un id nella selezione. */
  onToggleSelect?: (id: string) => void;
  /** Toggle "tutti i visibili" (la lista materials gia' filtrata). */
  onToggleSelectAll?: () => void;
}

const MaterialsTable: React.FC<MaterialsTableProps> = ({
  materials,
  formatGroups,
  onEditMaterial,
  sortField,
  sortDirection,
  onSortChange,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
}) => {
  const showSelection = !!selectedIds && !!onToggleSelect;
  const allVisibleSelected =
    showSelection && materials.length > 0 && materials.every((m) => selectedIds!.has(m.id));
  const someVisibleSelected =
    showSelection && materials.some((m) => selectedIds!.has(m.id)) && !allVisibleSelected;
  const [editingMaterial, setEditingMaterial] = React.useState<DatabaseMaterial | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = React.useState(false);
  const [comparatorAnchor, setComparatorAnchor] = React.useState<DatabaseMaterial | null>(null);
  const [reportMaterial, setReportMaterial] = React.useState<DatabaseMaterial | null>(null);
  const { mutate: deleteMaterial, isPending: isDeleting } = useDeleteMaterial();
  const { mutate: duplicateMaterial, isPending: isDuplicating } = useMaterialDuplicate();
  const { data: org } = useOrgProfile();
  const { currentOrganizationId } = useCurrentOrganization();
  const { data: familiesById } = useSupplierFamilies();
  const { hiddenSet } = useHiddenMaterials();
  const { mutate: toggleHidden } = useToggleHiddenMaterial();
  const { isAdmin } = useIsAppAdmin();
  const { usageMap: certifiedUsage } = useCertifiedUsage();
  const { mutate: forkMaterial, isPending: isForking } = useForkMaterial();

  /** Materiale di catalogo usato in pareti certificate: identità tecnica protetta. */
  const certifiedCount = (id: string) => certifiedUsage.get(id) ?? 0;

  const handleForkMaterial = (material: DatabaseMaterial) => {
    const n = certifiedCount(material.id);
    if (n > 0) {
      toast.error('Materiale usato in pareti certificate', {
        description: `È impiegato in ${n} ${n === 1 ? 'parete certificata' : 'pareti certificate'}: i dati tecnici non sono modificabili. Puoi però personalizzare prezzo e tempo di posa.`,
      });
      return;
    }
    const ok = window.confirm(
      `Portare "${material.name}" nella tua azienda?\n\n` +
      `• Ne verrà creata una copia modificabile in tutto (nome, descrizione, dati tecnici)\n` +
      `• L'originale di catalogo verrà nascosto dalla tua lista\n\n` +
      `Nota: la copia NON riceverà più gli aggiornamenti del listino fornitore.\n` +
      `Se ti serve solo cambiare prezzo o tempo, usa invece "Personalizzazione" nella scheda.`,
    );
    if (!ok) return;
    forkMaterial(material);
  };

  // ADMIN di piattaforma: può eliminare un materiale del catalogo GLOBALE (org NULL,
  // non di sistema). Operazione che riguarda TUTTE le org → richiede conferma esplicita.
  const canAdminDeleteGlobal = (material: DatabaseMaterial) =>
    isAdmin && !material.organization_id && !(material as { is_system?: boolean }).is_system;

  const handleAdminDeleteGlobal = (material: DatabaseMaterial) => {
    const ok = window.confirm(
      `⚠️ OPERAZIONE DI PIATTAFORMA\n\n` +
      `Stai per ELIMINARE dal catalogo GLOBALE:\n"${material.name}" (${material.code})\n\n` +
      `Sparirà per TUTTE le organizzazioni, non solo la tua.\n` +
      `Se è usato in una stratigrafia, il database bloccherà l'operazione.\n\n` +
      `Procedere?`,
    );
    if (!ok) return;
    deleteMaterial(material.id, {
      onSuccess: () => toast.success('Materiale globale eliminato'),
      onError: (error: Error) => toast.error(`Impossibile eliminare: ${error.message}`),
    });
  };
  // Un materiale è eliminabile solo se appartiene alla propria org: quelli globali
  // (organization_id NULL, catalogo condiviso) sono protetti dalla RLS.
  const canDeleteMaterial = (material: DatabaseMaterial) =>
    !!material.organization_id && material.organization_id === currentOrganizationId;
  const hasPdfBranding = useHasFeature('pdfBranding');
  const [exportingId, setExportingId] = React.useState<string | null>(null);
  const { wasteMap } = useWasteFactors();
  // Quante volte il materiale e' usato su TUTTA la piattaforma: serve a capire
  // se si puo' eliminare senza rompere i preventivi di un'altra azienda.
  const { usageMap, isReady: usageReady } = useMaterialsUsage();

  const handleExportPdf = async (material: DatabaseMaterial) => {
    try {
      setExportingId(material.id);
      // Solo: no logo/header personalizzato (passa null come org).
      await exportMaterialPdf({ material, org: hasPdfBranding ? (org ?? null) : null });
      toast.success('Scheda prodotto esportata');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Errore inatteso';
      toast.error(`Export PDF fallito: ${msg}`);
    } finally {
      setExportingId(null);
    }
  };

  const getCategoryLabel = (category: MaterialCategory) => {
    switch (category) {
      case 'board':
        return 'Lastra';
      case 'structure_frame':
        return 'Montante';
      case 'structure_guide':
        return 'Guida';
      case 'insulation':
        return 'Isolante';
      case 'accessory':
        return 'Accessorio';
      case 'screw':
        return 'Vite';
      case 'finish':
        return 'Finitura';
      case 'other':
        return 'Altro';
      default:
        return 'Sconosciuta';
    }
  };

  const calculateCostPerSqm = (material: DatabaseMaterial) => {
    const incidence = material.incidence_per_sqm || 1;
    // net_price arriva dalla view materials_with_pricing (calcolato live: list × catena famiglia × extra)
    // Fallback a unit_price per materiali pre-view, fallback ulteriore a list_price.
    const finalPrice =
      (material.net_price ?? material.unit_price ?? material.list_price ?? 0);
    return formatNumber(finalPrice * incidence);
  };

  const getSortIcon = (field: MaterialSortField) => {
    if (!sortField || sortField !== field) return <ArrowUpDown size={16} className="inline ml-1 text-zinc-300" />;
    if (sortDirection === 'asc') return <ArrowUp size={16} className="inline ml-1 text-primary" />;
    return <ArrowDown size={16} className="inline ml-1 text-primary" />;
  };

  const handleSort = (field: MaterialSortField) => {
    if (onSortChange) onSortChange(field);
  };

  const handleDeleteMaterial = (id: string) => {
    // F25.2 — guard lato client per materiali di sistema (starter library).
    // Il DB ha comunque un trigger BEFORE DELETE che blocca i record is_system=true,
    // ma checkiamo qui per evitare la chiamata di rete e dare feedback immediato.
    const material = materials.find(m => m.id === id);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((material as any)?.is_system) {
      toast.error('Materiale di sistema non cancellabile', {
        description: 'Fa parte della libreria starter di DryConfig. Per non usarlo, escludilo dalle tue stratigrafie.',
      });
      return;
    }
    if (material && !canDeleteMaterial(material)) {
      toast.error('Materiale del catalogo condiviso', {
        description: 'Non appartiene alla tua azienda: puoi duplicarlo per averne una copia modificabile, ma non eliminarlo.',
      });
      return;
    }
    deleteMaterial(
      id,
      {
        onSuccess: () => {
          toast.success('Materiale eliminato con successo!');
        },
        onError: (error: Error) => {
          toast.error(`Errore durante l'eliminazione: ${error.message}`);
        },
      }
    );
  };

  const handleDuplicateMaterial = (material: DatabaseMaterial) => {
    duplicateMaterial(material);
  };

  const handleEditMaterial = (material: DatabaseMaterial) => {
    if (onEditMaterial) {
      onEditMaterial(material);
    } else {
      setEditingMaterial(material);
    }
  };

  const handleMaterialUpdated = () => {
    setEditingMaterial(null);
  };

  const handleMaterialCreated = () => {
    setIsCreateDialogOpen(false);
  };

  const renderMaterialDetails = (material: DatabaseMaterial) => {
    if (material.category === 'board') {
      return (
        <div>
          <div className="font-medium">{material.name}</div>
          {material.material_type && (
            <div className="text-sm text-muted-foreground">
              Tipo: {material.material_type}
            </div>
          )}
          {material.fire_class && (
            <div className="text-sm text-muted-foreground">
              Classe fuoco: {material.fire_class}
            </div>
          )}
          {material.description && (
            <div className="text-sm text-muted-foreground truncate max-w-xs">
              {material.description}
            </div>
          )}
        </div>
      );
    }
    if (material.category === 'screw') {
      return (
        <div>
          <div className="font-medium">{material.name}</div>
          {material.box_pieces && (
            <div className="text-xs text-blue-800 font-mono">
              {material.box_pieces} pz/scatola
            </div>
          )}
          {Array.isArray(material.compatible_board_types) && material.compatible_board_types.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {material.compatible_board_types.map((matType, i) => (
                <span
                  key={i}
                  className="bg-blue-100 text-blue-800 rounded px-2 py-0.5 text-[11px] font-medium whitespace-nowrap"
                >
                  {matType}
                </span>
              ))}
            </div>
          )}
          {material.description && (
            <div className="text-xs text-muted-foreground mt-1">
              {material.description}
            </div>
          )}
        </div>
      );
    }
    if (material.category === 'other') {
      return (
        <div>
          <div className="font-medium">{material.name}</div>
          {material.description && (
            <div className="text-sm text-muted-foreground truncate max-w-xs">
              {material.description}
            </div>
          )}
          {material.mechanical_performance && (
            <div className="text-xs text-teal-700">Meccaniche: {material.mechanical_performance}</div>
          )}
          {material.thermal_performance_notes && (
            <div className="text-xs text-blue-700">Termo-igrometriche: {material.thermal_performance_notes}</div>
          )}
          {material.sustainability_notes && (
            <div className="text-xs text-green-600">Sostenibilità: {material.sustainability_notes}</div>
          )}
          {material.system_compatibility && (
            <div className="text-xs text-indigo-600">Compatibilità: {material.system_compatibility}</div>
          )}
          {material.fire_performance_notes && (
            <div className="text-xs text-orange-600">Fuoco: {material.fire_performance_notes}</div>
          )}
        </div>
      );
    }
    return (
      <div>
        <div className="font-medium">{material.name}</div>
        {material.description && (
          <div className="text-sm text-muted-foreground truncate max-w-xs">
            {material.description}
          </div>
        )}
      </div>
    );
  };

  const renderDimensions = (material: DatabaseMaterial) => {
    if (material.category === 'screw') {
      return material.thickness ? `Lunghezza: ${material.thickness} mm` : '-';
    }
    if (material.category === 'board') {
      return (
        <div className="text-sm">
          {material.width && material.length && material.thickness && (
            <div>
              {material.width} × {material.length} × {material.thickness} mm
            </div>
          )}
          {material.density && (
            <div className="text-muted-foreground">
              Densità: {material.density} kg/m³
            </div>
          )}
        </div>
      );
    }
    if (material.category === 'other') {
      if (material.is_variable_thickness) {
        return "Spessore variabile";
      }
      return material.thickness ? `${material.thickness} mm` : '-';
    }
    return material.thickness ? `${material.thickness} mm` : '-';
  };

  if (!materials.length) {
    return (
      <EmptyState
        icon={<Star className="h-7 w-7" />}
        title="Nessun materiale trovato"
        description="Modifica i filtri di ricerca, oppure aggiungi/importa nuovi materiali al catalogo."
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              {showSelection && (
                <TableHead className="w-10">
                  <Checkbox
                    checked={allVisibleSelected || (someVisibleSelected ? 'indeterminate' : false)}
                    onCheckedChange={() => onToggleSelectAll?.()}
                    aria-label="Seleziona tutti i visibili"
                  />
                </TableHead>
              )}
              <TableHead className="w-10" />
              <TableHead
                role="button"
                className="cursor-pointer select-none"
                onClick={() => handleSort('code')}
              >
                Codice {getSortIcon('code')}
              </TableHead>
              <TableHead
                role="button"
                className="cursor-pointer select-none"
                onClick={() => handleSort('name')}
              >
                Materiale {getSortIcon('name')}
              </TableHead>
              <TableHead
                role="button"
                className="cursor-pointer select-none"
                onClick={() => handleSort('category')}
              >
                Categoria {getSortIcon('category')}
              </TableHead>
              <TableHead
                role="button"
                className="cursor-pointer select-none"
                onClick={() => handleSort('supplier')}
              >
                Fornitore {getSortIcon('supplier')}
              </TableHead>
              <TableHead title="Famiglia di prodotto del fornitore. I materiali senza famiglia sono evidenziati: non ereditano lo sconto famiglia.">
                Famiglia
              </TableHead>
              <TableHead>
                Dimensioni
              </TableHead>
              <TableHead
                role="button"
                className="cursor-pointer select-none"
                onClick={() => handleSort('unit_price')}
              >
                Prezzo Unitario {getSortIcon('unit_price')}
              </TableHead>
              <TableHead
                role="button"
                className="cursor-pointer select-none"
                onClick={() => handleSort('family_discount_pct')}
                title="Sconto della famiglia di prodotto (definito in Fornitori → famiglia). Clicca per ordinare — materiali senza famiglia in fondo."
              >
                Sconto Famiglia % {getSortIcon('family_discount_pct')}
              </TableHead>
              <TableHead
                role="button"
                className="cursor-pointer select-none"
                onClick={() => handleSort('discount')}
                title="Sconto totale applicato (famiglia + extra prodotto)"
              >
                Sconto Totale % {getSortIcon('discount')}
              </TableHead>
              <TableHead
                role="button"
                className="cursor-pointer select-none"
                onClick={() => handleSort('incidence_per_sqm')}
              >
                Incidenza {getSortIcon('incidence_per_sqm')}
              </TableHead>
              <TableHead
                role="button"
                className="cursor-pointer select-none"
                onClick={() => handleSort('installation_time_per_sqm')}
                title="Tempo di posa in minuti. Ordina crescente per vedere in cima i materiali senza tempo."
              >
                Tempo di posa {getSortIcon('installation_time_per_sqm')}
              </TableHead>
              <TableHead
                role="button"
                className="cursor-pointer select-none"
                onClick={() => handleSort('cost_per_sqm')}
              >
                Costo/m² {getSortIcon('cost_per_sqm')}
              </TableHead>
              <TableHead title="Sfrido % applicato alla RDA (quantità da ordinare al fornitore). Override per questo materiale o default categoria.">
                Sfrido %
              </TableHead>
              <TableHead title="Oneri di discarica: costo di smaltimento del materiale, in percentuale sul suo costo. Oggi il valore e' registrato ma non entra ancora nel prezzo del preventivo.">
                Discarica %
              </TableHead>
              <TableHead title="Il materiale e' usato in qualche parete, preventivo o livello di finitura, su tutta la piattaforma (non solo nella tua azienda). Se e' libero puoi eliminarlo.">
                Utilizzato
              </TableHead>
              <TableHead className="w-40">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {materials.map((material) => (
              <TableRow key={material.id} className={[
                showSelection && selectedIds!.has(material.id) ? 'bg-primary/5' : '',
                hiddenSet.has(material.id) ? 'opacity-45' : '',
              ].filter(Boolean).join(' ')}>
                {showSelection && (
                  <TableCell className="p-1">
                    <Checkbox
                      checked={selectedIds!.has(material.id)}
                      onCheckedChange={() => onToggleSelect!(material.id)}
                      aria-label="Seleziona per modifica in massa"
                    />
                  </TableCell>
                )}
                <TableCell className="p-1">
                  <FavoriteToggle materialId={material.id} />
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="font-mono text-xs">
                    {material.code}
                  </Badge>
                  {(() => {
                    const fmts = formatGroups?.get(material.id);
                    if (!fmts || fmts.length < 2) return null;
                    return (
                      <Badge
                        variant="secondary"
                        className="ml-1 text-[10px] whitespace-nowrap"
                        title={`Varianti di formato (stesso prezzo): ${fmts.join(', ')}. Il codice esatto si sceglie nella distinta materiali.`}
                      >
                        {fmts.length} formati
                      </Badge>
                    );
                  })()}
                </TableCell>
                <TableCell>
                  <MaterialDetailsCell material={material} />
                  {certifiedCount(material.id) > 0 && (
                    <Badge
                      variant="outline"
                      className="mt-1 text-[10px] border-slate-400 text-slate-600 dark:text-slate-300 whitespace-nowrap"
                      title={`Usato in ${certifiedCount(material.id)} pareti certificate: non eliminabile e con dati tecnici protetti (la certificazione dipende da questi valori). Prezzo e tempo restano personalizzabili.`}
                    >
                      <Lock className="h-2.5 w-2.5 mr-1 inline" />
                      {certifiedCount(material.id)} {certifiedCount(material.id) === 1 ? 'parete cert.' : 'pareti cert.'}
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant="secondary">
                    {getCategoryLabel(material.category)}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm">{material.supplier}</TableCell>
                <TableCell>
                  {material.family_id ? (
                    <Badge variant="outline" className="text-xs max-w-[180px] truncate" title={familiesById?.get(material.family_id)?.name ?? material.family_code ?? ''}>
                      {familiesById?.get(material.family_id)?.name ?? material.family_code ?? '—'}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-xs border-amber-500 text-amber-600 dark:text-amber-400" title="Materiale senza famiglia: non eredita lo sconto famiglia. Assegna una famiglia in Fornitori.">
                      ⚠ Nessuna famiglia
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  <MaterialDimensionsCell material={material} />
                </TableCell>
                <TableCell>
                  {(() => {
                    const netPrice = material.net_price ?? material.unit_price ?? 0;
                    const listPrice = material.list_price ?? netPrice;
                    const isDiscounted = listPrice > netPrice + 0.001;
                    const isScrewBox = material.category === 'screw' && material.unit === 'scatola' && (material.box_pieces ?? 0) > 0;
                    const pricePerPiece = isScrewBox ? getScrewPricePerPiece(material) : null;
                    return (
                      <div className="flex flex-col items-start gap-0.5">
                        <Badge
                          variant="outline"
                          className={material.has_price_override
                            ? 'bg-primary/10 text-primary font-medium border-primary/40'
                            : 'bg-green-50 text-green-700 font-medium'}
                          title={material.has_price_override
                            ? `Prezzo personalizzato dalla tua azienda. Da catalogo sarebbe €${formatNumber(material.catalog_net_price ?? 0, 4)}.`
                            : (material.has_family_discount
                                ? 'Prezzo netto: listino × sconto famiglia'
                                : 'Prezzo di listino (nessuno sconto famiglia impostato)')}
                        >
                          {material.has_price_override && <span className="mr-1">✏️</span>}
                          €{formatNumber(netPrice)}/{material.unit}
                        </Badge>
                        {isScrewBox && pricePerPiece != null && (
                          <span className="text-[10px] text-blue-700 font-mono">
                            €{formatNumber(pricePerPiece, 4)}/pz · {material.box_pieces} pz/scatola
                          </span>
                        )}
                        {isDiscounted && (
                          <span className="text-[10px] text-muted-foreground">
                            Listino <span className="line-through">€{formatNumber(listPrice)}</span>
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </TableCell>
                {/* Colonna Sconto Famiglia % — evidenzia se mancante per cleanup catalogo */}
                <TableCell>
                  {(() => {
                    const familyPct = material.family_discount_pct ?? 0;
                    const hasFamily = !!(material as { family_id?: string | null }).family_id;
                    if (!hasFamily) {
                      return (
                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-[10px]" title="Nessuna famiglia associata al materiale">
                          ⚠ Famiglia mancante
                        </Badge>
                      );
                    }
                    if (familyPct < 0.01) {
                      return (
                        <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 text-[10px]" title="Famiglia associata ma sconto non impostato in Fornitori">
                          Famiglia ok · sconto 0%
                        </Badge>
                      );
                    }
                    return (
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200 font-medium">
                        {formatNumber(familyPct)}%
                      </Badge>
                    );
                  })()}
                </TableCell>
                <TableCell>
                  {(() => {
                    const totalPct = material.total_discount_pct ?? null;
                    const familyPct = material.family_discount_pct ?? 0;
                    const extraPct  = material.extra_discount_pct  ?? 0;
                    if (totalPct == null || totalPct < 0.01) {
                      return <Badge variant="outline" className="bg-gray-50 text-gray-500">—</Badge>;
                    }
                    return (
                      <div className="flex flex-col items-start gap-0.5">
                        <Badge variant="outline" className="bg-yellow-50 text-yellow-800 font-medium">
                          {formatNumber(totalPct)}%
                        </Badge>
                        {extraPct > 0 && (
                          <span className="text-[10px] text-muted-foreground">
                            Famiglia {formatNumber(familyPct, 1)}% + extra {formatNumber(extraPct, 1)}%
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="bg-blue-50 text-blue-700">
                    {formatNumber(material.incidence_per_sqm ?? 1, 3)} {material.unit}/m²
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="bg-purple-50 text-purple-700">
                    {material.installation_time_per_sqm ?
                      // DB in ore → mostra in minuti (×60)
                      `${formatNumber(material.installation_time_per_sqm * 60)} min/${material.unit}` :
                      '-'
                    }
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="bg-construction-light text-construction-primary font-medium">
                    €{calculateCostPerSqm(material)}/m²
                  </Badge>
                </TableCell>
                {/* Colonna Sfrido %: override esplicito (verde) o fallback default categoria (grigio) */}
                <TableCell>
                  {(() => {
                    const override = material.waste_percentage;
                    const hasOverride = override !== null && override !== undefined && Number.isFinite(Number(override));
                    if (hasOverride) {
                      return (
                        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 font-mono" variant="outline" title="Override esplicito su questo materiale (vince sul default categoria)">
                          {formatNumber(override, 1)}%
                        </Badge>
                      );
                    }
                    const fallback = wasteMap[material.category] ?? 0;
                    return (
                      <Badge variant="outline" className="bg-gray-50 text-gray-600 border-gray-200 font-mono" title={`Default categoria '${material.category}' da Impostazioni → Sfridi`}>
                        {formatNumber(fallback, 1)}% <span className="ml-1 text-[10px] opacity-70">def.</span>
                      </Badge>
                    );
                  })()}
                </TableCell>
                {/* Colonna Discarica %: stesso schema dello Sfrido — valore
                    esplicito sul materiale, altrimenti il default. Oggi TUTTI e
                    3.028 i materiali valgono 4: e' un default mai toccato, non
                    un dato, ed e' il motivo per cui diventera' un default d'org
                    con override sul singolo prodotto. */}
                <TableCell>
                  {(() => {
                    const val = material.disposal_percentage;
                    const hasValue = val !== null && val !== undefined && Number.isFinite(Number(val));
                    if (!hasValue) {
                      return (
                        <Badge variant="outline" className="bg-gray-50 text-gray-600 border-gray-200 font-mono" title="Nessun valore: userà il default dell'organizzazione">
                          — <span className="ml-1 text-[10px] opacity-70">def.</span>
                        </Badge>
                      );
                    }
                    return (
                      <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 font-mono" title="Oneri di discarica registrati su questo materiale">
                        {formatNumber(val, 1)}%
                      </Badge>
                    );
                  })()}
                </TableCell>
                {/* Utilizzato: conteggio su tutta la piattaforma. Finche' il dato non
                    e' arrivato non si scrive nulla — dire "libero" a vuoto porterebbe
                    a cancellare qualcosa che serve a un'altra azienda. */}
                <TableCell>
                  {(() => {
                    if (!usageReady) return <span className="text-xs text-muted-foreground">…</span>;
                    const n = usageMap.get(material.id) ?? 0;
                    return n > 0 ? (
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200" title={`Usato ${n} volte: non eliminabile senza rompere qualcosa`}>
                        Sì <span className="ml-1 opacity-70 font-mono">{n}</span>
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="bg-gray-50 text-gray-500 border-gray-200" title="Non usato da nessuna parte: si puo' eliminare">
                        No
                      </Badge>
                    );
                  })()}
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditMaterial(material)}
                      title="Modifica materiale"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDuplicateMaterial(material)}
                      disabled={isDuplicating}
                      title="Duplica materiale"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setComparatorAnchor(material)}
                      title="Confronta con altri fornitori"
                    >
                      <Scale className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleExportPdf(material)}
                      disabled={exportingId === material.id}
                      title="Scarica scheda prodotto (PDF)"
                    >
                      <FileDown className="h-4 w-4" />
                    </Button>
                    {/* Segnala al catalogo: solo sui materiali condivisi */}
                    {!material.organization_id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setReportMaterial(material)}
                        className="text-muted-foreground"
                        title="Segnala un problema su questo materiale di catalogo (prezzo errato, dato mancante, fuori produzione…)"
                      >
                        <Flag className="h-4 w-4" />
                      </Button>
                    )}
                    {/* "Personalizza completamente": solo sui materiali di catalogo
                        NON usati in pareti certificate. Duplica nell'org + nasconde
                        l'originale, così l'org può cambiare anche nome/dati tecnici. */}
                    {!material.organization_id && certifiedCount(material.id) === 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleForkMaterial(material)}
                        disabled={isForking}
                        className="text-muted-foreground"
                        title="Personalizza completamente: porta questo materiale nella tua azienda (modificabile in tutto) e nascondi l'originale di catalogo"
                      >
                        <UserPlus className="h-4 w-4" />
                      </Button>
                    )}
                    {/* Nascondi/Mostra: disponibile su OGNI materiale (filtro di vista
                        per-org, reversibile, non elimina). Sostituisce il vecchio lucchetto:
                        anche i materiali del catalogo condiviso ora hanno un'azione utile. */}
                    {(() => {
                      const isHidden = hiddenSet.has(material.id);
                      return (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleHidden({ materialId: material.id, isHidden })}
                          className="text-muted-foreground"
                          title={isHidden
                            ? 'Nascosto dal tuo catalogo: clicca per mostrarlo di nuovo'
                            : 'Nascondi dal tuo catalogo (non elimina, reversibile)'}
                        >
                          {isHidden ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                        </Button>
                      );
                    })()}
                    {/* Elimina: sui materiali della propria org. I materiali globali/
                        condivisi non si eliminano — per toglierli dalla tua vista usa "Nascondi". */}
                    {canDeleteMaterial(material) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteMaterial(material.id)}
                        className="text-destructive hover:text-destructive"
                        title="Elimina materiale"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                    {/* ADMIN piattaforma: elimina dal catalogo GLOBALE (icona distinta +
                        conferma). Riguarda tutte le org, quindi separata dal cestino normale. */}
                    {!canDeleteMaterial(material) && canAdminDeleteGlobal(material) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleAdminDeleteGlobal(material)}
                        className="text-amber-600 hover:text-amber-700 dark:text-amber-500"
                        title="ADMIN: elimina dal catalogo globale (riguarda TUTTE le organizzazioni)"
                      >
                        <ShieldAlert className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Dialog per creazione rapida */}
      <CreateMaterialDialog 
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onSuccess={handleMaterialCreated}
      />

      {editingMaterial && (
        <EditMaterialDialog
          material={editingMaterial}
          open={!!editingMaterial}
          onOpenChange={(open) => !open && setEditingMaterial(null)}
          onSuccess={handleMaterialUpdated}
        />
      )}

      <MaterialComparatorDialog
        open={!!comparatorAnchor}
        onOpenChange={(open) => !open && setComparatorAnchor(null)}
        anchor={comparatorAnchor}
      />

      <ReportMaterialDialog
        material={reportMaterial}
        open={!!reportMaterial}
        onOpenChange={(open) => !open && setReportMaterial(null)}
      />
    </div>
  );
};

export default MaterialsTable;

const FavoriteToggle: React.FC<{ materialId: string }> = ({ materialId }) => {
  const { favoritesSet } = useFavoriteMaterials();
  const toggle = useToggleFavoriteMaterial();
  const isFavorite = favoritesSet.has(materialId);
  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-7 w-7"
      onClick={(e) => {
        e.stopPropagation();
        toggle.mutate({ materialId, isFavorite });
      }}
      disabled={toggle.isPending}
      title={isFavorite ? 'Rimuovi dai preferiti' : 'Aggiungi ai preferiti'}
      aria-label={isFavorite ? 'Rimuovi dai preferiti' : 'Aggiungi ai preferiti'}
    >
      <Star className={`h-4 w-4 ${isFavorite ? 'fill-amber-400 text-amber-500' : 'text-muted-foreground'}`} />
    </Button>
  );
};
